import { apiFetch } from '@/lib/api-client'

/**
 * #1029 — günlük ürün hakkı: durum katmanı. Sayımı sunucu yapar; burası yalnız okur.
 * `GET /api/extension/serp-quota` yanıtı: `{ success, data: { enabled, unlimited, limit, used, remaining, resets_at } }`.
 * Yönetici anahtarı kapalıyken `{ enabled: false }`, sınırsız kullanıcıda `{ enabled: true, unlimited: true }` gelir.
 */
export interface SerpQuotaStatus {
  enabled: boolean
  unlimited: boolean
  limit: number | null
  used: number | null
  remaining: number | null
  resetsAt: string | null
  /** Limited modda sunucu sayısal Free sınırlarını yollarsa dolar; yoksa null (kırılma yok). */
  freeLimits: FreeLimits | null
}

export type FreeLimitKey =
  | 'favorites'
  | 'keyword_lists'
  | 'rank_tracker_products'
  | 'rank_tracker_keywords' // eski toplam alan: okunur ama kelime sayacı için KULLANILMAZ
  | 'rank_tracker_keywords_per_product'
export type FreeLimits = Partial<Record<FreeLimitKey, number>>

export const SERP_QUOTA_QUERY_KEY = ['serp-quota'] as const

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

const FREE_LIMIT_KEYS: readonly FreeLimitKey[] = [
  'favorites',
  'keyword_lists',
  'rank_tracker_products',
  'rank_tracker_keywords',
  'rank_tracker_keywords_per_product',
]

function parseFreeLimits(raw: unknown): FreeLimits | null {
  if (!raw || typeof raw !== 'object') return null
  const out: FreeLimits = {}
  for (const key of FREE_LIMIT_KEYS) {
    const v = num((raw as Record<string, unknown>)[key])
    if (v !== null) out[key] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

/** Ham `data` nesnesini doğrular. Bilinmeyen/bozuk biçim → null (çağıran kısıtlama göstermez). */
export function parseQuotaStatus(raw: unknown): SerpQuotaStatus | null {
  if (!raw || typeof raw !== 'object') return null
  const q = raw as Record<string, unknown>
  if (typeof q.enabled !== 'boolean') return null
  const resetsAt = typeof q.resets_at === 'string' ? q.resets_at : null
  if (!q.enabled) {
    return { enabled: false, unlimited: false, limit: null, used: null, remaining: null, resetsAt: null, freeLimits: null }
  }
  if (q.unlimited === true) {
    return { enabled: true, unlimited: true, limit: null, used: null, remaining: null, resetsAt: null, freeLimits: null }
  }
  const limit = num(q.limit)
  const used = num(q.used)
  const remaining = num(q.remaining)
  if (limit === null || used === null || remaining === null) return null
  return { enabled: true, unlimited: false, limit, used, remaining, resetsAt, freeLimits: parseFreeLimits(q.free_limits) }
}

/** Durum ucunu okur; zarf (`data`) açılır. Biçim bozuksa null; ağ/HTTP hatası fırlatılır. */
export async function fetchSerpQuotaStatus(): Promise<SerpQuotaStatus | null> {
  const res = await apiFetch<{ success?: unknown; data?: unknown } | null>('/extension/serp-quota')
  if (!res || res.success !== true) return null
  return parseQuotaStatus(res.data)
}

/** Yenilenmeye kalan süre: "14 sa 20 dk" / "20 dk" / "1 dk'dan az". Geçmiş/bozuk tarih → null. */
export function formatResetsIn(resetsAt: string | null | undefined, now: Date = new Date()): string | null {
  if (!resetsAt) return null
  const t = Date.parse(resetsAt)
  if (!Number.isFinite(t)) return null
  const ms = t - now.getTime()
  if (ms <= 0) return null
  const totalMin = Math.ceil(ms / 60_000)
  if (ms < 60_000) return "1 dk'dan az"
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m} dk`
  return m === 0 ? `${h} sa` : `${h} sa ${m} dk`
}

/** Yenilenme saati, yerel saatle: "14:32". Bozuk tarih → null. */
export function formatResetsAt(resetsAt: string | null | undefined): string | null {
  if (!resetsAt) return null
  const d = new Date(resetsAt)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false })
}
